import * as React from 'react';
import { commands } from '../utils/skipLinkHotkeys';
import { getDocument } from 'office-ui-fabric-react/lib/Utilities';
import { IButton } from 'office-ui-fabric-react/lib/Button';
import { IsFocusVisibleClassName } from 'office-ui-fabric-react/lib/Utilities';
import { useKeydownHandler } from 'owa-hotkeys';
import { observer } from 'mobx-react-lite';
import SkipLinkButton from './SkipLinkButton';

let styles = require('./SkipLinkControl.scss');
let classNames = require('classnames/bind').bind(styles);

const SkipLinkNameAttribute = 'data-skip-link-name';

export interface ISkipLinkControl {
    focus(): void;
}

/**
 * A control for parsing landmark regions on a page and creating skip links to make tabbable shortcut buttons to jump to those regions.
 */
export default observer(
    function SkipLinkControl(props: {}, ref: React.Ref<ISkipLinkControl>) {
        const buttonMap = React.useRef<IButton[]>([]);
        const focusedButtonIndex = React.useRef<number>();
        const [visible, setVisible] = React.useState(false);
        const rootRef = React.useRef<HTMLDivElement>();

        React.useImperativeHandle(ref, () => ({
            focus: () => {
                let button = buttonMap.current[0];
                if (button) {
                    button.focus();
                }
            },
        }));

        useKeydownHandler(rootRef, commands.focusSkipLinks, () => {
            // using ctrl+f6 from the skip link control should cycle through the skip links just like tab
            let index =
                focusedButtonIndex.current == buttonMap.current.length - 1
                    ? 0
                    : focusedButtonIndex.current + 1;
            let button = buttonMap.current[index];
            if (button) {
                button.focus();
            }
        });

        const defaultButtonMapRefCallback = React.useCallback((button: IButton, index: number) => {
            buttonMap.current[index] = button;
        }, []);

        const onBlur = React.useCallback((evt: React.FocusEvent<HTMLElement>) => {
            if (
                !(
                    evt.currentTarget &&
                    evt.relatedTarget &&
                    evt.currentTarget.contains &&
                    (evt.relatedTarget as HTMLElement).contains &&
                    evt.currentTarget.contains(evt.relatedTarget as HTMLElement)
                )
            ) {
                // if the relatedTarget is one of the other buttons, no need to set state to invisible
                setVisible(false);
            }
        }, []);

        const onButtonFocus = React.useCallback((index: number) => {
            focusedButtonIndex.current = index;
            setVisible(true);
        }, []);

        // when the skip link control starts rendering, search the DOM to find all elements that are marked as skip links
        // the dom might have changed since mounting or last rendering if other skip link items were rendered, so this is necessary
        let itemLinkButtons: JSX.Element[] = [];

        let currentDoc: Document = getDocument();
        let skipLinkElements = currentDoc
            ? currentDoc.querySelectorAll('[' + SkipLinkNameAttribute + ']')
            : null;

        for (let i = 0; i < skipLinkElements.length; i++) {
            let item = skipLinkElements.item(i);
            let name = item.getAttribute(SkipLinkNameAttribute);

            if (item instanceof HTMLElement) {
                itemLinkButtons.push(
                    <SkipLinkButton
                        key={name}
                        name={name}
                        index={i}
                        item={item}
                        shouldAutoFocus={i == 0}
                        visible={visible}
                        onFocus={onButtonFocus}
                        componentRef={defaultButtonMapRefCallback}
                    />
                );
            }
        }

        return (
            <div
                className={classNames(
                    styles.wrapper,
                    visible ? styles.visible : styles.invisible,
                    // manually manage Fabric focus styling to simulate tabbing when cycling through skip links using ctrl+f6
                    visible && IsFocusVisibleClassName
                )}
                onBlur={onBlur}>
                {itemLinkButtons}
            </div>
        );
    },
    { forwardRef: true }
);
